import { FC, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  FormControl,
  FormLabel,
  CircularProgress,
  TextField,
} from '@mui/material';
import { Phone } from '@phosphor-icons/react';
import ReusableModal from '../common/Modal';
import PhoneInput from 'react-phone-input-2';
import { commonStyles } from '../../utils/styles/inputs';
import { createOutboundCall, getPlivoOutboundCall } from '../../utils/apis';
import { useMutation } from '@tanstack/react-query';

interface PhoneModalProps {
  open: boolean;
  onClose: () => void;
  agentId: string;
  agentKey: string;
  showAlert: (message: string, severity: 'error' | 'success') => void;
}

const PhoneModal: FC<PhoneModalProps> = ({
  open,
  onClose,
  agentKey,
  showAlert,
}) => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [userName, setUserName] = useState('');

  const { mutate: createOutboundCallMutation, isPending: isCreatingCall } =
    useMutation({
      mutationFn: createOutboundCall,
      onSuccess: () => {
        showAlert(
          'Call triggered successfully! You will receive a call shortly.',
          'success'
        );
        onClose();
        setPhoneNumber('');
        setUserName('');
      },
      onError: (error: any) => {
        showAlert(
          error?.response?.data?.message ||
            'Failed to trigger call. Please try again.',
          'error'
        );
        onClose();
      },
    });

  const { mutate: getPlivoOutboundCallMutation, isPending: isPlivoCall } =
    useMutation({
      mutationFn: getPlivoOutboundCall,
      onSuccess: () => {
        showAlert(
          'Call triggered successfully! You will receive a call shortly.',
          'success'
        );
        onClose();
        setPhoneNumber('');
        setUserName('');
      },
      onError: (error: any) => {
        showAlert(
          error?.response?.data?.message ||
            'Failed to trigger call. Please try again.',
          'error'
        );
        onClose();
      },
    });

  const handleSubmit = () => {
    if (phoneNumber.startsWith('91')) {
      getPlivoOutboundCallMutation({
        phoneNumber: `+${phoneNumber}`,
        agentKey,
        userName,
      });
    } else {
      createOutboundCallMutation({
        phoneNumber: `+${phoneNumber}`,
        agentKey,
        userName,
      });
    }
  };

  return (
    <ReusableModal
      open={open}
      onClose={onClose}
      title="Enter Your Phone Number"
      subtitle="Our assistant will call you shortly to assist you"
      width={400}
    >
      <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <FormControl>
          <FormLabel sx={commonStyles.formLabel}>Your Name</FormLabel>
          <TextField
            fullWidth
            placeholder="Enter your name"
            value={userName}
            onChange={e => setUserName(e.target.value)}
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                height: '40px',
              },
            }}
          />
        </FormControl>

        <FormControl>
          <FormLabel sx={commonStyles.formLabel}>Phone Number</FormLabel>
          <PhoneInput
            country={'in'}
            placeholder=""
            value={phoneNumber}
            onChange={phone => setPhoneNumber(phone)}
            inputStyle={{
              width: '100%',
              height: '40px',
              fontSize: '16px',
            }}
            containerStyle={{
              flex: 1,
            }}
            specialLabel={''}
          />
        </FormControl>

        <Divider />
        <Button
          fullWidth
          variant="contained"
          startIcon={<Phone size={16} />}
          onClick={handleSubmit}
          disabled={
            phoneNumber.length < 10 ||
            !userName.trim() ||
            isCreatingCall ||
            isPlivoCall
          }
          sx={{
            textTransform: 'none',
            borderRadius: '6px',
          }}
        >
          {isCreatingCall || isPlivoCall ? (
            <CircularProgress size={16} />
          ) : (
            'Talk to Assistant'
          )}
        </Button>
      </Box>
    </ReusableModal>
  );
};

export default PhoneModal;
